import { gql, makeVar, TypedDocumentNode } from "@apollo/client";
import { useApolloClient, useMutation, useReactiveVar } from "@apollo/client/react";
import { useCallback, useEffect, useMemo } from "react";

import { CompletionReason } from "../__generated__/globalTypes";
import { safeCalculatePercentage } from "../apollo/typePolicies/CourseProgress";
import { CourseQuery_instance_progress_lastView } from "../pages/Player/__generated__/CourseQuery";
import { emptyProgress } from "../util/fakeCourse";
import { downloadCertificate } from "../util/pdfService";
import { reportErrorToNR } from "../util/reportErrorToNR";
import { getSecondsFromTimestamp } from "../util/time";
import { CourseProgressFragment } from "./__generated__/CourseProgressFragment";
import { InstanceProgressFragment, InstanceProgressFragment_progress } from "./__generated__/InstanceProgressFragment";
import { SaveCompletion, SaveCompletionVariables } from "./__generated__/SaveCompletion";
import { SlideViewsQuery } from "./__generated__/SlideViewsQuery";
import { UpdateProgress, UpdateProgressVariables } from "./__generated__/UpdateProgress";
import { useAppState } from "./useAppState";
import { ErrorSeverity, useErrorHandler } from "./useErrorHandler";

export const noCourseError = "no course";

export const LOCAL_COURSE_PROGRESS_FRAGMENT = gql`
  fragment CourseProgressFragment on Course {
    complete
    sections {
      modules {
        id
        name
        fullNumber
        type
        quizScore
        duration
        progress @client {
          viewedSlides
          totalSlides
          viewed
          total
        }
      }
    }
  }
`;

export const LOCAL_COURSE_COMPLETION_FRAGMENT = gql`
  fragment CourseCompletionFragment on Course {
    completion {
      dateTimeCompleted
      reason
    }
  }
`;

export const LOCAL_INSTANCE_PROGRESS_FRAGMENT = gql`
  fragment InstanceProgressFragment on TrainingInstance {
    progress {
      instanceId
      viewedSeconds
      totalSeconds
      viewedSlides
      totalSlides
      totalQuizzes
      passedQuizzes
      complete
      hoursRemaining @client
      percentSlides @client
      percentQuizzes @client
      lastView {
        slideId
        resumeTime
        dateTimeViewed
      }
      moduleProgress {
        id
        name
        type
        fullNumber
        viewedSlides
        totalSlides
        quizScore
      }
    }
  }
`;

export const TRAINING_EXPIRATION_FRAGMENT = gql`
  fragment TrainingExpirationFragment on RosterUser {
    expiration @client {
      expirationText
      expirationDate
      isExpired
    }
  }
`;

export const INSTANCE_EXPIRATION_FRAGMENT = gql`
  fragment InstanceExpirationFragment on TrainingInstance {
    expiration @client {
      expirationText
      expirationDate
      isExpired
    }
  }
`;

export const SAVE_COMPLETION_QUERY: TypedDocumentNode<SaveCompletion, SaveCompletionVariables> = gql`
  mutation SaveCompletion($courseUuid: String!, $trainingId: Int!, $reason: CompletionReason!) {
    saveCompletion(courseUuid: $courseUuid, trainingId: $trainingId, reason: $reason) {
      reason
    }
  }
`;

export const START_COURSE_MUTATION = gql`
  mutation StartCourseMutation($courseId: Int!) {
    startCourse(courseId: $courseId) {
      id
      userType
      registrationDeadline
      expires
      orderId
      created
      certification {
        name
        initials
        imageUrl
      }
      giacAvailable
      giacPurchased
      shippingTracking {
        deliveryStatus
        trackingNumber
      }
      trainingInstance {
        id
        expirationDateTime
        userId
        progress {
          instanceId
          viewedSeconds
          totalSeconds
          viewedSlides
          totalSlides
          totalQuizzes
          passedQuizzes
          complete
          moduleProgress {
            id
            name
            type
            fullNumber
            viewedSlides
            totalSlides
            quizScore
          }
        }
      }
      course {
        id
        trainingId
        name
        hasMyLabs
        quickstartGuideUrl
        shortName
        completion {
          dateTimeCompleted
          reason
        }
        licenses {
          title
          url
        }
        handouts {
          title
          url
        }
      }
    }
  }
`;

export const UPDATE_PROGRESS_QUERY: TypedDocumentNode<UpdateProgress, UpdateProgressVariables> = gql`
  mutation UpdateProgress($courseProgress: CourseProgressInput!) {
    updateProgress(courseProgress: $courseProgress) {
      userId
      instanceId
      slides {
        slideId
        resumeTime
        dateTimeViewed
      }
    }
  }
`;

export const SLIDE_VIEWS_QUERY = gql`
  query SlideViewsQuery($courseId: Int!, $instanceId: String) {
    course(courseId: $courseId) {
      trainingId
      sections {
        id
        modules {
          id
          slides {
            id
            moduleId
            duration
            lastView(instanceId: $instanceId) {
              slideId
              resumeTime
              dateTimeViewed
            }
          }
        }
      }
    }
  }
`;

export const progressVar = makeVar<{
  [instanceId: string]: InstanceProgressFragment_progress | undefined;
}>({});

// Tracks whether slide views are being fetched in the background for accurate progress calculation
export const slideViewsLoadingVar = makeVar<boolean>(false);

export function useCourseProgress(courseId?: number, courseUuid?: string, instanceId?: string, rosterUserId?: string) {
  const { throwError, reportError } = useErrorHandler();
  const { module, refreshModule } = useAppState();

  const client = useApolloClient();
  const {
    userId,
    appState: { slide, moduleId },
  } = useAppState();

  const progress = useReactiveVar(progressVar);

  const expiration = useMemo(() => {
    if (rosterUserId) {
      const trainingExpirationFragment = client.readFragment({
        id: `RosterUser:${rosterUserId}`,
        fragment: TRAINING_EXPIRATION_FRAGMENT,
      });

      return trainingExpirationFragment?.expiration;
    } else if (instanceId) {
      const instanceExpirationFragment = client.readFragment({
        id: `TrainingInstance:${instanceId}`,
        fragment: INSTANCE_EXPIRATION_FRAGMENT,
      });

      return instanceExpirationFragment?.expiration;
    }
  }, [client, rosterUserId, instanceId]);

  const completion = useMemo(() => {
    if (!instanceId) return;

    const instanceProgressFragment = client.readFragment<InstanceProgressFragment>({
      id: `TrainingInstance:${instanceId}`,
      fragment: LOCAL_INSTANCE_PROGRESS_FRAGMENT,
    });

    const instanceProgress = instanceProgressFragment?.progress;
    return instanceProgress?.complete;
  }, [client, instanceId]);

  const saveCompletion = useCallback(
    async (reason: CompletionReason): Promise<void> => {
      if (!courseId) return throwError(noCourseError);

      await client.mutate({
        mutation: SAVE_COMPLETION_QUERY,
        variables: {
          trainingId: courseId,
          courseUuid,
          reason,
        },
        update: cache =>
          cache.modify({
            id: `Course:{"trainingId":${courseId}}`,
            fields: {
              completion: () => ({ dateTimeCompleted: new Date(), reason }),
              complete: () => true,
            },
          }),
      });
    },
    [courseId, client, throwError, courseUuid],
  );

  const syncCourseSlideViews = useCallback(async () => {
    const queryResult = client.cache.readQuery<SlideViewsQuery>({
      query: SLIDE_VIEWS_QUERY,
      variables: { courseId, instanceId },
      returnPartialData: true,
    });

    const sections = queryResult?.course?.sections ?? [];
    const slides = sections.flatMap(section => section.modules).flatMap(module => module.slides);

    const [courseViewedSeconds, courseViewedSlides] = slides.reduce(
      (acc, slide) => {
        const [seconds, slideCount] = acc;
        return [
          slide.lastView ? seconds + getSecondsFromTimestamp(slide.duration) : seconds,
          slide.lastView ? slideCount + 1 : slideCount,
        ];
      },
      [0, 0],
    );

    client.cache.modify({
      id: `CourseProgress:{"instanceId":"${instanceId}"}`,
      fields: {
        viewedSeconds: () => courseViewedSeconds,
        viewedSlides: () => courseViewedSlides,
      },
    });
  }, [client, courseId, instanceId]);

  // Helper: Apply self-healing for invalid slide data when no module context
  const applySelfHealingForLibrary = (
    instanceProgress: InstanceProgressFragment_progress,
    recalculatedTotalSlides: number,
    recalculatedViewedSlides: number,
  ) => {
    const cachedTotalSlides = instanceProgress.totalSlides || 0;
    const cachedViewedSlides = instanceProgress.viewedSlides || 0;

    const hasInvalidSlideData =
      (recalculatedTotalSlides > 0 && recalculatedTotalSlides !== cachedTotalSlides) ||
      (recalculatedViewedSlides > 0 && recalculatedViewedSlides !== cachedViewedSlides);

    if (hasInvalidSlideData) {
      return {
        ...instanceProgress,
        totalSlides: recalculatedTotalSlides > 0 ? recalculatedTotalSlides : cachedTotalSlides,
        viewedSlides: recalculatedViewedSlides > 0 ? recalculatedViewedSlides : cachedViewedSlides,
      };
    }

    return instanceProgress;
  };

  // Helper: Derive per-module progress live from slide-level data.
  //
  // Each module's `progress` @client field is computed by sumProgress (see util/cache.ts),
  // which walks the module's slides and, from each slide's `duration` and `lastView`,
  // produces viewedSlides/totalSlides and viewed/total seconds in a single pass. Because
  // all four come from the same slide read, they cannot drift from one another. This mirrors
  // the server aggregation in ondemand-lambdas (sync.ts), which likewise sums slide durations.
  //
  // IMPORTANT: per-slide `lastView` is NOT hydrated by InitCoursePageQuery on load — it arrives
  // via the deferred SlideViewsQuery (~2.5s later) and only for loaded modules. Until then the
  // live counts read as zero. Progress is monotonic (a viewed slide never becomes unviewed), so
  // we floor every derived value at the server baseline (moduleProgress from InitCoursePageQuery).
  // This keeps the server value until live data hydrates and exceeds it, preventing the first
  // updateProgress after a refresh from overwriting good server progress with zeros.
  const deriveModuleProgress = (
    instanceProgress: InstanceProgressFragment_progress,
    courseSections: CourseProgressFragment["sections"] | undefined,
  ) => {
    const liveModules = courseSections?.flatMap(section => section.modules) ?? [];
    const liveModuleById = new Map(liveModules.map(module => [module.id, module]));

    const moduleProgress = instanceProgress.moduleProgress.map(moduleProgress => {
      const liveModule = liveModuleById.get(moduleProgress.id);
      const liveProgress = liveModule?.progress;
      return {
        ...moduleProgress,
        viewedSlides: Math.max(liveProgress?.viewedSlides ?? 0, moduleProgress.viewedSlides ?? 0),
        totalSlides: Math.max(liveProgress?.totalSlides ?? 0, moduleProgress.totalSlides ?? 0),
        quizScore: liveModule?.quizScore ?? moduleProgress.quizScore,
      };
    });

    return { liveModules, moduleProgress };
  };

  // Helper: Apply self-healing when moduleProgress is empty but instanceProgress has valid cached totals
  const applySelfHealingForEmptyModuleProgress = useCallback(
    (
      instanceProgress: InstanceProgressFragment_progress,
      lastView?: CourseQuery_instance_progress_lastView,
    ): InstanceProgressFragment_progress | null => {
      if (instanceProgress.moduleProgress.length > 0) return null;

      const hasValidCachedTotals = instanceProgress.totalSlides > 0 && instanceProgress.totalSeconds > 0;
      if (!hasValidCachedTotals) return null;

      console.warn("Empty moduleProgress detected, using instanceProgress cached totals", {
        totalSlides: instanceProgress.totalSlides,
        totalSeconds: instanceProgress.totalSeconds,
        courseId,
        instanceId,
      });

      return {
        ...instanceProgress,
        lastView: lastView ?? instanceProgress.lastView,
      };
    },
    [courseId, instanceId],
  );

  // Helper: Handle fallback chain when progress totals are invalid
  const handleInvalidProgressTotals = useCallback(
    (
      progress: InstanceProgressFragment_progress,
      instanceProgress: InstanceProgressFragment_progress,
      lastView?: CourseQuery_instance_progress_lastView,
    ): InstanceProgressFragment_progress => {
      console.warn("Invalid progress totals detected in getCourseProgress", {
        totalSlides: progress.totalSlides,
        totalSeconds: progress.totalSeconds,
        moduleProgressLength: progress.moduleProgress?.length || 0,
      });

      // Try to use last known good values from progressVar
      const lastKnownProgress = instanceId ? progressVar()[instanceId] : undefined;
      if (
        lastKnownProgress &&
        lastKnownProgress.totalSlides > 0 &&
        lastKnownProgress.totalSeconds > 0 &&
        lastKnownProgress.moduleProgress?.length > 0
      ) {
        console.warn("Using last known good progress values from progressVar", {
          cachedTotalSlides: progress.totalSlides,
          cachedTotalSeconds: progress.totalSeconds,
          lastKnownTotalSlides: lastKnownProgress.totalSlides,
          lastKnownTotalSeconds: lastKnownProgress.totalSeconds,
        });
        return {
          ...lastKnownProgress,
          lastView: lastView ?? lastKnownProgress.lastView,
          complete: progress.complete,
        };
      }

      // Try instanceProgress cached values as final fallback
      if (instanceProgress.totalSlides > 0 && instanceProgress.totalSeconds > 0) {
        console.warn("Using instanceProgress cached totals as final fallback", {
          calculatedTotalSlides: progress.totalSlides,
          calculatedTotalSeconds: progress.totalSeconds,
          cachedTotalSlides: instanceProgress.totalSlides,
          cachedTotalSeconds: instanceProgress.totalSeconds,
        });
        return {
          ...instanceProgress,
          lastView: lastView ?? instanceProgress.lastView,
          complete: progress.complete,
        };
      }

      // No fallback available - throw error to prevent using invalid data
      const error = new Error(
        "Cannot get course progress: invalid totals (totalSlides=" +
          progress.totalSlides +
          ", totalSeconds=" +
          progress.totalSeconds +
          ", moduleProgress.length=" +
          (progress.moduleProgress?.length || 0) +
          ") and no last known good data available",
      );
      console.error(error.message, { progress, lastKnownProgress, courseId, instanceId });

      reportError(ErrorSeverity.MEDIUM, "Failed to get valid course progress data", {
        error,
        totalSlides: progress.totalSlides,
        totalSeconds: progress.totalSeconds,
        moduleProgressLength: progress.moduleProgress?.length || 0,
        courseId,
        instanceId,
      });

      throw error;
    },
    [courseId, instanceId, reportError],
  );

  const getCourseProgress = useCallback(
    (lastView?: CourseQuery_instance_progress_lastView) => {
      // Ensure the just-viewed slide's lastView is in the cache before we read the
      // course fragment, so the live per-slide progress derivation below includes it.
      // (readFragment snapshots the computed `progress` field, so the write must happen first.)
      if (lastView?.slideId) {
        client.cache.modify({
          id: `Slide:${lastView.slideId}`,
          fields: { lastView: () => lastView },
        });
      }

      const courseProgressFragment = client.readFragment<CourseProgressFragment>({
        id: `Course:{"trainingId":${courseId}}`,
        fragment: LOCAL_COURSE_PROGRESS_FRAGMENT,
      });

      const courseSections = courseProgressFragment?.sections;

      const instanceProgressFragment = client.readFragment<InstanceProgressFragment>({
        id: `TrainingInstance:${instanceId}`,
        fragment: LOCAL_INSTANCE_PROGRESS_FRAGMENT,
      });

      const instanceProgress = instanceProgressFragment?.progress;

      if (!instanceProgress) return;

      // Recalculate totalSlides and viewedSlides from moduleProgress (self-healing)
      const recalculatedTotalSlides = instanceProgress.moduleProgress.reduce((sum, m) => sum + m.totalSlides, 0);
      const recalculatedViewedSlides = instanceProgress.moduleProgress.reduce((sum, m) => sum + m.viewedSlides, 0);

      // If no module context (Library page), return self-healed progress
      if (!module) {
        return applySelfHealingForLibrary(instanceProgress, recalculatedTotalSlides, recalculatedViewedSlides);
      }

      // Self-healing: If moduleProgress is empty but instanceProgress has valid totals, use cached values
      const emptyModuleProgressFallback = applySelfHealingForEmptyModuleProgress(instanceProgress, lastView);
      if (emptyModuleProgressFallback) return emptyModuleProgressFallback;

      // Derive per-module counts live from slide data (slide duration + lastView).
      const { liveModules, moduleProgress } = deriveModuleProgress(instanceProgress, courseSections);

      // Aggregate all four totals from the same live slide traversal so viewed/total
      // slides and seconds always share a basis (remaining time can't drift from % complete).
      // Each aggregate is floored at the server baseline (see deriveModuleProgress) so that
      // unhydrated slide data (before the deferred SlideViewsQuery lands) can never regress
      // progress below what the server already knows.
      const totalSlides = moduleProgress.reduce((sum, module) => sum + module.totalSlides, 0);
      const viewedSlides = moduleProgress.reduce((sum, module) => sum + module.viewedSlides, 0);

      const liveTotalSeconds = liveModules.reduce((sum, module) => sum + (module.progress?.total ?? 0), 0);
      const liveViewedSeconds = liveModules.reduce((sum, module) => sum + (module.progress?.viewed ?? 0), 0);

      const totalSeconds = Math.max(liveTotalSeconds, instanceProgress.totalSeconds || 0);
      const viewedSeconds = Math.max(liveViewedSeconds, instanceProgress.viewedSeconds || 0);

      const progress = {
        ...instanceProgress,
        viewedSeconds,
        totalSeconds,
        complete: courseProgressFragment?.complete ?? instanceProgress.complete,
        passedQuizzes: moduleProgress.reduce(
          (sum, module) => sum + (module.quizScore && module.quizScore >= 80 ? 1 : 0),
          0,
        ),
        totalQuizzes: moduleProgress.filter(module => module.type === "QUIZ").length,
        totalSlides,
        viewedSlides,
        lastView: lastView ?? instanceProgress.lastView,
        moduleProgress,
        sections: undefined,
      };

      // Validate totals before returning - use fallback if invalid
      const hasInvalidTotals = !progress.totalSlides || !progress.totalSeconds;
      if (hasInvalidTotals) {
        return handleInvalidProgressTotals(progress, instanceProgress, lastView);
      }

      return progress;
    },
    [client, courseId, instanceId, module, applySelfHealingForEmptyModuleProgress, handleInvalidProgressTotals],
  );

  const updateProgress = useCallback(
    async (moduleRefresh: boolean, lastView?: CourseQuery_instance_progress_lastView) => {
      if (!courseId || !instanceId) return;

      let progress;
      try {
        progress = getCourseProgress(lastView);
      } catch (error) {
        // getCourseProgress throws when it can't return valid data
        // Report to New Relic with mutation context before re-throwing
        await reportErrorToNR(
          new Error("Invalid progress totals when sending updated course progress from OD Player"),
          {
            error,
            courseId,
            instanceId,
            userId,
            moduleId,
            slideId: slide?.id,
            moduleRefresh,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
            url: typeof window !== "undefined" ? window.location.href : undefined,
            referrer: typeof document !== "undefined" ? document.referrer : undefined,
          },
        ).catch(() => {
          // Never block progress updates on NR reporting
        });
      }

      if (!progress) return;

      const percentSlides = safeCalculatePercentage(progress.viewedSlides, progress.totalSlides);
      const percentQuizzes = safeCalculatePercentage(progress.passedQuizzes, progress.totalQuizzes);

      if (!progress.complete) {
        if (percentSlides >= 80) {
          await saveCompletion(CompletionReason.SLIDES_VIEWED);
          progress.complete = true;
        } else if (percentQuizzes >= 80) {
          await saveCompletion(CompletionReason.QUIZZES_PASSED);
          progress.complete = true;
        }
      }

      progressVar({
        ...progressVar(),
        [instanceId]: progress,
      });

      client.cache.modify({
        id: `TrainingInstance:${instanceId}`,
        fields: {
          progress: () => progress,
        },
      });

      // Note: the just-viewed slide's lastView is written into the cache by
      // getCourseProgress (above) before the live progress is derived.

      if (moduleRefresh) refreshModule();

      const courseProgress = {
        ...progress,
        courseId,
        instanceId,
        userId,
        lastView: { ...progress.lastView, __typename: undefined },
        total: undefined,
        viewed: undefined,
        percent: undefined,
        hoursRemaining: undefined,
        percentQuizzes: undefined,
        percentSlides: undefined,
        __typename: undefined,
        moduleProgress: progress.moduleProgress.map(module => ({
          ...module,
          percentSlides: undefined,
          __typename: undefined,
        })),
      };

      return await client.mutate({
        mutation: UPDATE_PROGRESS_QUERY,
        variables: {
          courseProgress: {
            ...courseProgress,
          },
        },
      });
    },
    [courseId, client, instanceId, userId, moduleId, slide?.id, getCourseProgress, refreshModule, saveCompletion],
  );

  const openCert = useCallback(async () => {
    if (!courseId) return throwError(noCourseError);
    if (!instanceId) return throwError("no instance");

    try {
      const { url } = await downloadCertificate(courseId, instanceId);
      window.open(url, "_blank");
    } catch (error) {
      await reportError(ErrorSeverity.LOW, "Error downloading certificate", {
        error,
        courseId,
        courseUuid,
        instanceId,
      });
      throw error;
    }
  }, [throwError, reportError, courseId, courseUuid, instanceId]);

  const [startCourse] = useMutation(START_COURSE_MUTATION);

  const updateCourseProgressState = useCallback(async () => {
    if (!courseId || !instanceId) return;

    const progress = getCourseProgress();

    client.cache.modify({
      id: `TrainingInstance:${instanceId}`,
      fields: {
        progress: () => progress,
      },
    });

    if (progress) {
      progressVar({
        ...progressVar(),
        [instanceId]: progress,
      });
    }
  }, [client, courseId, instanceId, getCourseProgress]);

  // Helper function to build course progress payload for service worker
  const buildCourseProgressPayload = useCallback(
    (lastView?: CourseQuery_instance_progress_lastView) => {
      if (!courseId || !instanceId || !userId) return null;

      let progress;
      try {
        progress = getCourseProgress(lastView);
      } catch (error) {
        // getCourseProgress throws when data is invalid and no fallback available
        console.warn("Cannot build course progress payload: invalid progress data", error);
        return null;
      }

      if (!progress) return null;

      return {
        ...progress,
        courseId,
        instanceId,
        userId,
        lastView: lastView ? { ...lastView, __typename: undefined } : progress.lastView,
        total: undefined,
        viewed: undefined,
        percent: undefined,
        hoursRemaining: undefined,
        percentQuizzes: undefined,
        percentSlides: undefined,
        __typename: undefined,
        moduleProgress: progress.moduleProgress.map(module => ({
          ...module,
          percentSlides: undefined,
          __typename: undefined,
        })),
      };
    },
    [courseId, instanceId, userId, getCourseProgress],
  );

  // Initialize progressVar from cache if not already set (ensures fallback exists)
  useEffect(() => {
    if (!instanceId) return;

    // Only initialize if progressVar doesn't have data for this instance
    if (progressVar()[instanceId]) return;

    const instanceProgressFragment = client.readFragment<InstanceProgressFragment>({
      id: `TrainingInstance:${instanceId}`,
      fragment: LOCAL_INSTANCE_PROGRESS_FRAGMENT,
    });

    const instanceProgress = instanceProgressFragment?.progress;

    if (instanceProgress && instanceProgress.totalSlides > 0 && instanceProgress.totalSeconds > 0) {
      progressVar({
        ...progressVar(),
        [instanceId]: instanceProgress,
      });
    }
  }, [instanceId, client]);

  return {
    progress: instanceId ? progress[instanceId] : emptyProgress,
    expiration,
    completion,
    openCert,
    updateCourseProgressState,
    startCourse,
    saveCompletion,
    updateProgress,
    getCourseProgress,
    syncCourseSlideViews,
    buildCourseProgressPayload,
  };
}
